// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import {
  Dispatch,
  SetStateAction,
  useEffect,
  useReducer,
  useState,
} from "react";
import axios, { AxiosRequestConfig } from "axios";

type FetchStateType =
  | "FETCH_READY"
  | "FETCH_INIT"
  | "FETCH_SUCCESS"
  | "FETCH_FAILURE";

export enum ErrorTypes {
  "NETWORK_ERROR",
}

interface FetchAction {
  type: FetchStateType;
  payload?: any;
  errorType?: ErrorTypes;
}

export interface DataFetchState {
  isLoading: boolean;
  isError: boolean;
  errorType?: ErrorTypes;
  data?: any;
}

const dataFetchReducer = (state: DataFetchState, action: FetchAction) => {
  switch (action.type) {
    case "FETCH_READY":
      return {
        ...state,
        isLoading: false,
        isError: false,
        errorType: undefined,
      };
    case "FETCH_INIT":
      return {
        ...state,
        isLoading: true,
        isError: false,
      };
    case "FETCH_SUCCESS":
      return {
        ...state,
        isLoading: false,
        isError: false,
        data: action.payload,
      };
    case "FETCH_FAILURE":
      return {
        ...state,
        isLoading: false,
        isError: true,
        errorType: action.errorType,
      };
    default:
      throw new Error();
  }
};

export const useDataFetchApi = (
  config?: AxiosRequestConfig
): [
  DataFetchState,
  Dispatch<SetStateAction<AxiosRequestConfig | undefined>>
] => {
  const [requestConfig, setRequestConfig] = useState<
    AxiosRequestConfig | undefined
  >(config);

  const [state, dispatch] = useReducer(dataFetchReducer, {
    isLoading: false,
    isError: false,
    data: null,
  });

  useEffect(() => {
    let didCancel = false;

    if (
      requestConfig === undefined ||
      (requestConfig.method === "post" && requestConfig.data === null)
    ) {
      return;
    }

    const fetchData = async () => {
      dispatch({ type: "FETCH_INIT" });

      try {
        const result = await axios(requestConfig);

        if (!didCancel) {
          dispatch({ type: "FETCH_SUCCESS", payload: result.data });
        }
      } catch (error) {
        if (!didCancel) {
          if (error.message === "Network Error") {
            dispatch({
              type: "FETCH_FAILURE",
              errorType: ErrorTypes.NETWORK_ERROR,
            });
          } else {
            dispatch({ type: "FETCH_FAILURE" });
          }
        }
      }
    };

    fetchData();

    return () => {
      didCancel = true;
    };
  }, [requestConfig]);

  return [state, setRequestConfig];
};

export default useDataFetchApi;
